import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Dialog,
  Grid,
  Slide,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import { Cancel, CheckCircleOutlineOutlined } from "@mui/icons-material";
import {
  SingleAlertInfo,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { inactivateActivateUser, useUserReducer } from "../../../redux/slice/userSlice";
import { providerRoleId } from "../../../constants/applicationConstants";
import { useModalStyles } from "../../common/useModalStyles";

interface InactiveUserProps {
  open: boolean;
  onClose: (reloadList: boolean) => void;
  userId: number;
  userRoleId: number;
  userStatus: string;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const InactiveUserPopup: React.FC<InactiveUserProps> = ({
  open,
  onClose,
  userId,
  userRoleId,
  userStatus,
}) => {
  const { handleSubmit } = useForm();
  const dispatch = useAppDispatch();
  const { status } = useUserReducer();
  const isUserActive = userStatus === "Active";

  const {
    tokens,
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
    destructiveBtnSx,
  } = useModalStyles();

  const handleAlert = (message: string, type: "success" | "error" | "info") => {
    const alertObj: SingleAlertInfo = { message, alertType: type };
    dispatch(setSingleAlertObj(alertObj));
  };

  const inactivateUserProfile = async (userId: number) => {
    try {
      const response = await dispatch(
        inactivateActivateUser({ userId, action: isUserActive ? "Inactivate" : "Activate" })
      );
      if (status === "success") {
        handleAlert(
          `User profile ${isUserActive ? "inactivated" : "activated"} successfully.`,
          "success"
        );
        return response;
      } else {
        throw new Error("Failed to inactivate user profile.");
      }
    } catch (error) {
      handleAlert("Failed to inactivate user profile.", "error");
      throw error;
    }
  };

  const onSubmit = async () => {
    await inactivateUserProfile(userId);
    onClose(true);
  };

  const handleClose = () => {
    onClose(false);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      onClick={(event) => event.stopPropagation()}
      PaperProps={{
        sx: {
          ...paperSx(false),
          width: 520,
          maxWidth: "90vw",
          color: tokens.textPrimary,
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>

        {/* ── Header ── */}
        <Box sx={headerSx}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            {isUserActive ? "Inactivate User Profile" : "Activate User Profile"}
          </Typography>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ ...bodySx, px: 3.5, py: 3.5 }}>
          <Grid container spacing={2}>

            {/* Confirmation question */}
            <Grid item xs={12}>
              <Typography sx={{ color: tokens.textPrimary, fontSize: "0.95rem" }}>
                Are you sure you want to{" "}
                <Typography component="span" sx={{ fontWeight: 700, color: tokens.textPrimary }}>
                  {isUserActive ? "inactivate" : "activate"}
                </Typography>{" "}
                this user?
              </Typography>
            </Grid>

            {/* Provider note — amber warning block, same as DeleteAgencyPopup */}
            {userRoleId === providerRoleId && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    background: "rgba(255, 176, 32, 0.08)",
                    border: "1px solid rgba(255, 176, 32, 0.22)",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                    color: "#ffc857",
                    fontSize: "14px",
                    lineHeight: 1.6,
                  }}
                >
                  {isUserActive
                    ? "Please note: Deactivating this user will also deactivate the associated practice provider."
                    : "Please note: Activating this user will also activate the associated practice provider."}
                </Box>
              </Grid>
            )}

          </Grid>
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerSx}>
          <Grid container justifyContent="flex-end" spacing={2}>
            
            {/* YES Button - Futuristic Light Green */}
            <Grid item xs={12} sm="auto">
              <Button
                type="submit"
                startIcon={<CheckCircleOutlineOutlined />}
                fullWidth
                sx={{
                  minWidth: { xs: "100%", sm: 135 },
                  backgroundColor: "#14B8A6",        
                  color: "#ffffff",
                  fontWeight: 600,
                  size: "small",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                  "&:hover": {
                    backgroundColor: "#0D9488",      
                    boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)",
                  },
                  "& .MuiTypography-root": { 
                    color: "#ffffff" 
                  },
                }}
              >
                Yes
              </Button>
            </Grid>

            {/* NO Button - Red */}
            <Grid item xs={12} sm="auto">
              <Button
                type="button"
                onClick={handleClose}
                variant="outlined"
                startIcon={<Cancel sx={{ fontSize: "1.1rem" }} />}
                sx={{
                  minWidth: { xs: "100%", sm: 135 },
                  borderColor: "#ef4444",
                  color: "#ef4444",
                  fontWeight: 600,
                  size: "small",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(239, 68, 68, 0.08)",
                    borderColor: "#ef4444",
                  },
                }}
              >
                No
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Dialog>
  );
};

export default InactiveUserPopup;